import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, extractError } from "../../lib/api";
import { useWS } from "../../context/WebSocketContext";
import { PageHeader, KpiTile, SectionCard, StatePill } from "../../components/Ui";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { ArrowRight, Package, Truck, Warehouse } from "lucide-react";

export default function ProductionLoadingSupervisorDashboard() {
  const { subscribe } = useWS();
  const [loadingQueue, setLoadingQueue] = useState([]);
  const [stockClosing, setStockClosing] = useState(0);
  const [stockLines, setStockLines] = useState(0);
  const [packLogsToday, setPackLogsToday] = useState(0);
  const [busy, setBusy] = useState(true);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [outward, stock, packs] = await Promise.all([
        api.get("/outward-dispatches/bootstrap", { params: { module: "loading" } }),
        api.get("/stacks/product-stock", { params: { positive_only: 1 } }),
        api.get("/production/logs", { params: { active_only: 1 } }).catch(() => ({ data: { results: [] } })),
      ]);
      const dispatches = outward.data?.dispatches || [];
      setLoadingQueue(dispatches.filter((d) => d.state === "WEIGH_TARE"));
      const list = stock.data?.results || [];
      setStockLines(list.length);
      setStockClosing(list.reduce((s, r) => s + (Number(r.bags_closing) || 0), 0));
      const logList = packs.data?.results || [];
      setPackLogsToday(logList.length);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const unsubs = [
      subscribe("outward_dispatch", load),
      subscribe("production", load),
      subscribe("production_batch", load),
    ];
    return () => unsubs.forEach((u) => u?.());
  }, [subscribe, load]);

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="production-loading-supervisor-dashboard">
      <PageHeader
        eyebrow="Production & Loading"
        title="Packing & Loading Desk"
        description="Confirm outward loading, log packed bags, and keep an eye on stock on hand."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button asChild className="bg-bansal-sage hover:bg-bansal-sageDark text-white min-h-11">
              <Link to="/sales/loading">
                <Truck className="h-4 w-4 mr-2" />
                Loading
              </Link>
            </Button>
            <Button asChild variant="outline" className="min-h-11">
              <Link to="/production/packing-register">
                <Package className="h-4 w-4 mr-2" />
                Packing
              </Link>
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <KpiTile
          label="Awaiting load"
          value={busy ? "…" : loadingQueue.length}
          sub="Tare done · ready to load"
          accent
          testId="pls-kpi-loading"
        />
        <KpiTile
          label="Stock on hand"
          value={busy ? "…" : stockClosing}
          sub={`${stockLines} pack-size line${stockLines === 1 ? "" : "s"}`}
          testId="pls-kpi-stock"
        />
        <KpiTile
          label="Recent packs"
          value={busy ? "…" : packLogsToday}
          sub="Open packing lines"
          testId="pls-kpi-packs"
        />
        <KpiTile
          label="Your pages"
          value="4"
          sub="Dashboard · Load · Pack · Stock"
          testId="pls-kpi-pages"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <DeskLink
          to="/sales/loading"
          icon={Truck}
          title="Loading"
          sub="Confirm bags on outward trucks after tare"
        />
        <DeskLink
          to="/production/packing-register"
          icon={Package}
          title="Packing"
          sub="Log bags packed by batch and pack size"
        />
        <DeskLink
          to="/stacks"
          icon={Warehouse}
          title="Stock on hand"
          sub="Packed product In / Out / Closing"
        />
      </div>

      <SectionCard title="Loading queue" testId="pls-loading-queue">
        {busy ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
        ) : loadingQueue.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            No trucks awaiting loading. When weighbridge records tare, they appear here.
          </p>
        ) : (
          <div className="space-y-2">
            {loadingQueue.slice(0, 10).map((d) => (
              <Link
                key={d.id}
                to={`/sales/loading?dispatch=${d.id}`}
                className="flex items-center justify-between gap-3 rounded-xl border border-border px-3 py-3 hover:bg-accent/60"
              >
                <div className="min-w-0">
                  <div className="font-mono text-sm">{d.code || `#${d.id}`}</div>
                  <div className="text-sm truncate">
                    {d.vehicle_number || "—"} · {d.party_name || "Party pending"}
                  </div>
                </div>
                <StatePill state={d.state} />
              </Link>
            ))}
          </div>
        )}
        <Link
          to="/sales/loading"
          className="inline-flex items-center gap-1 text-xs link-brand mt-3"
        >
          Open loading <ArrowRight className="h-3 w-3" />
        </Link>
      </SectionCard>
    </div>
  );
}

function DeskLink({ to, icon: Icon, title, sub }) {
  return (
    <Link
      to={to}
      className="flex items-start gap-3 rounded-xl border border-border bg-card px-4 py-3.5 hover:border-bansal-sage/50 transition-colors"
    >
      <span className="mt-0.5 rounded-lg bg-bansal-sage/10 p-2 text-primary">
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-foreground">{title}</span>
        <span className="block text-xs text-muted-foreground mt-0.5">{sub}</span>
      </span>
      <ArrowRight className="h-4 w-4 text-muted-foreground ml-auto mt-1 shrink-0" />
    </Link>
  );
}
